import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CoberturaAlertService } from '@core/services/cobertura-alert.service';
import { Posto } from '@core/models/models';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import {
  NotificationConnectionState,
  OcorrenciaNotification,
  OcorrenciaNotificationService,
} from '@core/services/ocorrencia-notification.service';

type AlertSection = 'all' | 'coverage' | 'occurrences';
type OccurrenceFilter = 'all' | 'unread' | 'urgent';

@Component({
  selector: 'app-alertas',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    EmptyStateComponent,
    PaginatorControlComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './alertas.component.html',
  styleUrl: './alertas.component.scss',
})
export class AlertasComponent implements OnInit {
  private readonly coberturaAlertSvc = inject(CoberturaAlertService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly notificationSvc = inject(OcorrenciaNotificationService);

  postosSemCobertura: Posto[] = [];
  ocorrencias: OcorrenciaNotification[] = [];
  connectionState: NotificationConnectionState = 'disconnected';
  activeSection: AlertSection = 'all';
  occurrenceFilter: OccurrenceFilter = 'all';
  searchTerm = '';
  refreshing = false;
  lastUpdatedAt = new Date();

  coverageFirst = 0;
  coverageRows = 5;
  occurrenceFirst = 0;
  occurrenceRows = 5;

  ngOnInit(): void {
    this.coberturaAlertSvc.postosSemCobertura$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((postos) => {
        this.postosSemCobertura = postos ?? [];
        this.ensureValidPages();
        this.lastUpdatedAt = new Date();
        this.cdr.markForCheck();
      });

    this.notificationSvc.occurrences$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ocorrencias) => {
        this.ocorrencias = ocorrencias ?? [];
        this.ensureValidPages();
        this.lastUpdatedAt = new Date();
        this.cdr.markForCheck();
      });

    this.notificationSvc.connectionState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.connectionState = state;
        this.cdr.markForCheck();
      });
  }

  get totalActiveAlerts(): number {
    return this.postosSemCobertura.length + this.unreadCount;
  }

  get unreadCount(): number {
    return this.ocorrencias.filter((item) => !item.lida).length;
  }

  get urgentCount(): number {
    return this.ocorrencias.filter(
      (item) => !item.lida && item.criticidadeEnum === 3,
    ).length;
  }

  get filteredPostos(): Posto[] {
    const term = this.normalizedSearch;
    if (!term) return this.postosSemCobertura;

    return this.postosSemCobertura.filter((posto) =>
      [
        posto.nome,
        posto.codigo,
        posto.nomeCliente,
        posto.endereco,
        posto.nomeMunicipio,
        posto.nomeProvincia,
      ].some((value) => this.matches(value, term)),
    );
  }

  get pagedPostos(): Posto[] {
    return this.filteredPostos.slice(
      this.coverageFirst,
      this.coverageFirst + this.coverageRows,
    );
  }

  get filteredOcorrencias(): OcorrenciaNotification[] {
    const term = this.normalizedSearch;

    return this.ocorrencias.filter((item) => {
      if (this.occurrenceFilter === 'unread' && item.lida) return false;
      if (this.occurrenceFilter === 'urgent' && item.criticidadeEnum !== 3) {
        return false;
      }

      if (!term) return true;
      return [
        `OC-${item.id}`,
        this.notificationSvc.tipoLabel(item),
        this.notificationSvc.criticidadeLabel(item.criticidadeEnum),
        item.nomePosto,
        item.nomeEfetivo,
      ].some((value) => this.matches(value, term));
    });
  }

  get pagedOcorrencias(): OcorrenciaNotification[] {
    return this.filteredOcorrencias.slice(
      this.occurrenceFirst,
      this.occurrenceFirst + this.occurrenceRows,
    );
  }

  get showCoverage(): boolean {
    return this.activeSection === 'all' || this.activeSection === 'coverage';
  }

  get showOccurrences(): boolean {
    return this.activeSection === 'all' || this.activeSection === 'occurrences';
  }

  get connectionLabel(): string {
    return this.notificationSvc.connectionStateLabel(this.connectionState);
  }

  setSection(section: AlertSection): void {
    this.activeSection = section;
    this.coverageFirst = 0;
    this.occurrenceFirst = 0;
  }

  setOccurrenceFilter(filter: OccurrenceFilter): void {
    this.occurrenceFilter = filter;
    this.occurrenceFirst = 0;
  }

  onSearch(value: string): void {
    this.searchTerm = value;
    this.coverageFirst = 0;
    this.occurrenceFirst = 0;
  }

  clearSearch(): void {
    this.onSearch('');
  }

  onCoveragePageChange(event: { first?: number; rows?: number }): void {
    this.coverageRows = event.rows ?? this.coverageRows;
    this.coverageFirst = event.first ?? 0;
  }

  onOccurrencePageChange(event: { first?: number; rows?: number }): void {
    this.occurrenceRows = event.rows ?? this.occurrenceRows;
    this.occurrenceFirst = event.first ?? 0;
  }

  marcarComoLida(id: number): void {
    this.notificationSvc.markAsRead(id);
  }

  marcarTodasComoLidas(): void {
    this.notificationSvc.markAllAsRead();
  }

  async refreshAll(): Promise<void> {
    if (this.refreshing) return;
    this.refreshing = true;
    this.coberturaAlertSvc.refresh();

    try {
      await this.notificationSvc.refresh();
    } catch {
      // O estado da ligação comunica a indisponibilidade sem bloquear a página.
    } finally {
      this.lastUpdatedAt = new Date();
      this.refreshing = false;
      this.cdr.markForCheck();
    }
  }

  postoLocation(posto: Posto): string {
    return (
      [posto.nomeMunicipio, posto.nomeProvincia].filter(Boolean).join(', ') ||
      posto.endereco ||
      'Localização não informada'
    );
  }

  occurrenceIcon(item: OcorrenciaNotification): string {
    if (item.criticidadeEnum === 3) return 'ri-alarm-warning-line';
    if (item.tipoOcorrenciaEnum === 2) return 'ri-tools-line';
    if (item.tipoOcorrenciaEnum === 3) return 'ri-shield-keyhole-line';
    return 'ri-shield-flash-line';
  }

  severityClass(value: number): string {
    if (value === 3) return 'urgent';
    if (value === 1) return 'high';
    return 'medium';
  }

  trackPosto(_: number, item: Posto): number | string {
    return item.id ?? item.codigo ?? item.nome;
  }

  trackOccurrence(_: number, item: OcorrenciaNotification): number {
    return item.id;
  }

  private get normalizedSearch(): string {
    return this.searchTerm.trim().toLocaleLowerCase('pt');
  }

  private matches(value: unknown, term: string): boolean {
    return String(value ?? '').toLocaleLowerCase('pt').includes(term);
  }

  private ensureValidPages(): void {
    if (this.coverageFirst >= this.filteredPostos.length) {
      this.coverageFirst = 0;
    }
    if (this.occurrenceFirst >= this.filteredOcorrencias.length) {
      this.occurrenceFirst = 0;
    }
  }
}
